import DownloadSOVButton from "./DownloadSOVButton";
import SovGrid from "./SovGrid";
import SovHeatmap from "./SovHeatmap";
import SovMetrics from "./SovMetrics";
import UploadSov from "./UploadSov";
import {
  storeSovExtractionData,
  retrieveSovExtractionData,
  retrieveSovOverrides,
} from "./utils/sovDataPersistence";
import {
  FilterListRounded as FilterIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import * as strings from "common/strings";
import Component from "components/Component";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import Visible from "components/common/Visible";
import { usePricing } from "components/customPricing/dnfModel1/pricingHooks";
import * as config from "config";
import * as fileUtils from "fileUtils";
import * as flags from "flags";
import { useSnackbar } from "notistack";
import { useMemo, useState, useCallback, useRef } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as aiAgentsService from "services/aiAgents";
import * as sovService from "services/sovService";
import * as staticDataService from "services/staticDataService";
import * as SOVStore from "store/SOV";
import * as analyticsStore from "store/analytics";
import { useSovAnalytics } from "store/hooks/sovHooks";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as utils from "utils";

// CSV Export Column Configuration (Source of truth for field mappings)
const CSV_COLUMNS = [
  { key: "_LOCNUM", name: "LOCNUM" },
  { key: "_STREET_ADDRESS", name: "Street Address" },
  { key: "_CITY", name: "City" },
  { key: "_STATE_CODE", name: "State Code" },
  { key: "_ADDRESS", name: "Address" },
  { key: "_ZIP_CODE", name: "Zip Code" },
  { key: "_LAT", name: "Lat" },
  { key: "_LNG", name: "Long" },
  { key: "_TIV", name: "TIV" },
  { key: "_BI", name: "BI" },
  { key: "_RISK_COUNT", name: "# of Risks" },
  { key: "_BI_LOAD", name: "BI Load" },
  { key: "_OCCUPANCY_SEGMENT", name: "Occupancy Segment" },
  { key: "_RISK_MANAGEMENT", name: "Risk Management" },
  { key: "_FIRE_PROTECTION", name: "Fire Protection" },
  { key: "_CONSTRUCTION", name: "Construction" },
  { key: "_OCCUPANCY_HAZARD", name: "Occupancy Hazard" },
  { key: "_OTHER_DETAILS", name: "Other Details" },
  { key: "_AUTO_RATE", name: "Auto Rate(%)" },
  { key: "_MANUAL_RATE", name: "Manual Rate(%)" },
  { key: "_RISK_ADJ", name: "Risk Adj(%)" },
  { key: "_COMMENT", name: "Comment" },
];

const ADDITIONAL_SOV_FIELDS = {
  County: "_COUNTY",
  "Year Built": "_YEAR_BUILT",
  "# of Stories": "_NUM_STORIES",
  "Square Footage": "_SQUARE_FOOTAGE",
  "Building Value": "_BUILDING_VALUE",
};

const SOV_FIELD_MAPPING = {
  ...Object.fromEntries(CSV_COLUMNS.map((col) => [col.name, col.key])),
  ...ADDITIONAL_SOV_FIELDS,
};

const transformSovFixerToModal = (sovFixerRow) => ({
  LOCNUM: sovFixerRow.location_group?.value || "",
  "Street Address": sovFixerRow.street?.value || "",
  City: sovFixerRow.city?.value || "",
  "State Code": sovFixerRow.state?.value || "",
  "Zip Code": sovFixerRow.zip?.value || "",
  County: sovFixerRow.county?.value || "",
  Address: sovFixerRow.full_address?.value || "",
  Lat: sovFixerRow.latitude?.value || "",
  Long: sovFixerRow.longitude?.value || "",
  TIV: sovFixerRow["limits__total_value"]?.value || 0,
  BI: sovFixerRow["limits__bi_limit"]?.value || 0,
  "# of Risks": sovFixerRow["const__num_buildings"]?.value || 1,
  "Year Built": sovFixerRow["const__bldg_year_built"]?.value || "",
  "# of Stories": sovFixerRow["const__num_stories"]?.value || "",
  "Square Footage": sovFixerRow["const__bldg_area"]?.value || "",
  "Building Value": sovFixerRow["limits__building_value"]?.value || 0,
  Construction: sovFixerRow.marmalade_construction || "",
  "Occupancy Segment": sovFixerRow.marmalade_occupancy || "",
  "BI Load": "",
  "Occupancy Hazard": "",
  "Risk Management": "",
  "Fire Protection": "",
});

const convertFromModalFormat = (row) => {
  const internalRow = {};
  Object.entries(SOV_FIELD_MAPPING).forEach(([modalKey, internalKey]) => {
    internalRow[internalKey] = row[modalKey];
  });
  return internalRow;
};

const useProgramPricing = ({ pricingMethod } = {}) => {
  pricingMethod = pricingMethod ?? "programPricing";

  const { isFetching } = usePricing({ pricingMethod });

  const programPricing = useSelector(analyticsStore.select)?.[pricingMethod];

  return {
    programPricing,
    isProgramPricingFetching: isFetching,
  };
};

const useBuildTableRows = ({ resolvedSov, programPricing, overrides }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const newRows = (resolvedSov?.locations ?? [])
      .map((row) => ({
        ...row,
        ...programPricing?.values?.sov?.[row.id],
      }))
      .map((row) => {
        const rowOverrides = overrides?.[row.id] ?? {};
        const overriddenValues = Object.fromEntries(
          Object.entries(rowOverrides).map(([key, override]) => [
            key,
            override.value,
          ])
        );

        return {
          ...row,
          ...overriddenValues,
          _GOOGLE_EARTH_LINK: makeGoogleEarthLink(row),
        };
      });
    setRows(newRows);
  }, [setRows, resolvedSov, programPricing, overrides]);

  return {
    rows,
  };
};

const makeGoogleEarthLink = ({ _ADDRESS }) => {
  if (!strings.hasValue(_ADDRESS)) {
    return null;
  }
  return `https://earth.google.com/web/search/${encodeURIComponent(_ADDRESS)}`;
};

const useBuildValidations = ({ config, rows, selectedLayerExcess }) => {
  const [validations, setValidations] = useState({});

  useEffect(() => {
    const validations = {};
    const isMissingValue = (column) => (row) =>
      row[column] == null || row[column] === "";
    rows?.forEach((row) => {
      const fieldErrors = Object.fromEntries(
        (config?.requiredColumns ?? [])
          .filter((columnSpec) =>
            Array.isArray(columnSpec)
              ? columnSpec.every((c) => isMissingValue(c)(row))
              : isMissingValue(columnSpec)(row)
          )
          .flatMap((columnSpec) =>
            Array.isArray(columnSpec)
              ? columnSpec.map((column) => [column, "Missing value"])
              : [[columnSpec, "Missing value"]]
          )
      );
      if (Object.keys(fieldErrors).length) {
        validations[row.id] = {
          fieldErrors,
          severity:
            config?.layerExcessColumn != null &&
            row[config?.layerExcessColumn] >= selectedLayerExcess
              ? "error"
              : "warning",
        };
      }
    });
    setValidations(validations);
  }, [config, rows, setValidations, selectedLayerExcess]);

  return {
    validations,
  };
};

const NonCatValidationSummary = ({
  validations,
  isFiltered,
  setIsFiltered,
}) => {
  const validationCount = Object.keys(validations ?? {}).length;
  return isFiltered || validationCount > 0 ? (
    <Chip
      color={"error"}
      variant={isFiltered ? "filled" : "outlined"}
      label={`${validationCount} ${
        validationCount === 1 ? "row" : "rows"
      } missing info for non-cat pricing`}
      clickable
      onClick={() => setIsFiltered(!isFiltered)}
      icon={<FilterIcon />}
    />
  ) : (
    <></>
  );
};

const DEFAULT_VALIDATIONS = {
  requiredColumns: [
    "_TIV",
    ["_OCCUPANCY_SEGMENT", "_MANUAL_RATE_PCT"],
    "_ADDRESS",
  ],
  layerExcessColumn: "_TIV",
};

const EXTRACTION_STATE_KEY = "sov_extraction_state";

const saveExtractionState = (submissionId, state) => {
  try {
    const allStates = JSON.parse(
      localStorage.getItem(EXTRACTION_STATE_KEY) || "{}"
    );
    allStates[submissionId] = {
      ...state,
      timestamp: Date.now(),
    };
    localStorage.setItem(EXTRACTION_STATE_KEY, JSON.stringify(allStates));
  } catch (error) {}
};

const getExtractionState = (submissionId) => {
  try {
    const allStates = JSON.parse(
      localStorage.getItem(EXTRACTION_STATE_KEY) || "{}"
    );
    const state = allStates[submissionId];
    if (state && Date.now() - state.timestamp > 24 * 60 * 60 * 1000) {
      clearExtractionState(submissionId);
      return null;
    }
    return state;
  } catch (error) {
    return null;
  }
};

const clearExtractionState = (submissionId) => {
  try {
    const allStates = JSON.parse(
      localStorage.getItem(EXTRACTION_STATE_KEY) || "{}"
    );
    delete allStates[submissionId];
    localStorage.setItem(EXTRACTION_STATE_KEY, JSON.stringify(allStates));
  } catch (error) {}
};

const SOV = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [isFilteredToNonCatErrors, setIsFilteredToNonCatErrors] = useState(
    false
  );
  const [validationLayerId, setValidationLayerId] = useState(null);
  const [extractionProgress, setExtractionProgress] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [locationsMenuAnchor, setLocationsMenuAnchor] = useState(null);

  const sovInput = useSelector(SOVStore.selectSOV);
  const layers = useSelector(pricingSelectors.selectResolvedLayers);

  const isInitialMount = useRef(true);
  const submissionId = window.location.pathname.split("/")[2];

  const { data: sovConfig } = staticDataService.useConfigQuery("sov");
  const { data: featureFlags } = staticDataService.useConfigQuery(
    "feature_flags"
  );

  const isPingExtractionEnabled =
    featureFlags?.[flags.USE_PING_SOV_EXTRACTION] ?? false;

  useEffect(() => {
    if (!isPingExtractionEnabled) return;

    const savedState = getExtractionState(submissionId);
    if (
      savedState &&
      savedState.extractionJobId &&
      !savedState.extractionComplete
    ) {
      if (sovInput?.extractionJobId !== savedState.extractionJobId) {
        dispatch(
          SOVStore.updateSOV({
            originalFilename: savedState.originalFilename,
            extractionJobId: savedState.extractionJobId,
            extractionComplete: false,
          })
        );
      }
      if (isInitialMount.current) {
        setExtractionProgress({
          status: "RESTORING",
          pct_complete: 0,
        });
      }
    }

    isInitialMount.current = false;
  }, [
    submissionId,
    dispatch,
    sovInput?.extractionJobId,
    isPingExtractionEnabled,
  ]);

  const hasLocalLocations = sovInput?._resolvedLocations;
  const shouldResolve = sovInput?.sovFileId && !hasLocalLocations;

  const {
    data: resolvedSov,
    isLoading: isResolvedSovFetching,
  } = sovService.useResolveSovQuery(
    { sovInput, geolocate: true },
    { skip: !shouldResolve }
  );

  const finalResolvedSov = hasLocalLocations
    ? { locations: sovInput._resolvedLocations }
    : resolvedSov;

  const { programPricing, isProgramPricingFetching } = useProgramPricing({
    pricingMethod: sovConfig?.pricingMethod ?? null,
  });

  useSovAnalytics();

  useEffect(() => {
    if (sovInput?.overridesFileId && !sovInput?.overrides) {
      retrieveSovOverrides(sovInput.overridesFileId).then((data) => {
        dispatch(SOVStore.updateSOV({ overrides: data }));
      });
    }
  }, [sovInput?.overridesFileId, sovInput?.overrides, dispatch]);

  const { rows } = useBuildTableRows({
    resolvedSov: finalResolvedSov,
    programPricing,
    overrides: sovInput?.overrides,
  });

  const selectedLayerExcess = useMemo(
    () =>
      layers
        ?.filter((layer) => layer.id === validationLayerId)
        ?.map((layer) => layer.attachment)?.[0] ?? 0,
    [layers, validationLayerId]
  );

  const { validations } = useBuildValidations({
    config: sovConfig?.validations ?? DEFAULT_VALIDATIONS,
    rows,
    selectedLayerExcess,
  });

  useEffect(() => {
    if (
      isFilteredToNonCatErrors &&
      Object.keys(validations ?? {}).length === 0
    ) {
      setIsFilteredToNonCatErrors(false);
    }
  }, [isFilteredToNonCatErrors, setIsFilteredToNonCatErrors, validations]);

  const filteredRows = isFilteredToNonCatErrors
    ? rows.filter(({ id }) => validations?.[id] != null)
    : rows;

  const fillColumn = (field, value, all, targetRowIds = null) => {
    const isBlank = (val) => val == null || val === "__NULL__" || val === "";
    const changeKeys = Object.fromEntries(
      Object.entries(rows).map(([k, v]) => {
        if (targetRowIds && !targetRowIds.has(v.id)) {
          return [v.id, false];
        }
        return [v.id, all || isBlank(v[field])];
      })
    );
    if (!all) {
      Object.entries(sovInput?.overrides || {}).forEach(([key, value]) => {
        if (value[field] != null) {
          changeKeys[key] = false;
        }
      });
    }
    const overrides = {
      ...sovInput?.overrides,
    };
    let updatedCount = 0;
    const totalRows = targetRowIds
      ? targetRowIds.size
      : Object.keys(changeKeys).length;
    Object.entries(changeKeys).forEach(([key, required]) => {
      if (required) {
        overrides[key] = {
          ...overrides[key],
          [field]: { value: value },
        };
        updatedCount++;
      }
    });

    dispatch(SOVStore.updateSOV({ overrides: overrides }));

    const columnName = columns.find((col) => col.key === field)?.name || field;
    enqueueSnackbar(
      `${updatedCount} of ${totalRows} ${columnName} items updated`,
      { autoHideDuration: 3000 }
    );
  };
  const updateOverrides = (newRow, oldRow) => {
    const existingRowOverrides = sovInput?.overrides?.[newRow.id] ?? {};

    const originalRow = finalResolvedSov?.locations?.find(
      (loc) => loc.id === newRow.id
    );

    const newOverrides = {
      overrides: {
        ...sovInput?.overrides,
        [newRow.id]: {
          ...existingRowOverrides,
          ...Object.fromEntries(
            Object.entries(newRow ?? {})
              .filter(([k, v]) => oldRow[k] !== v)
              .map(([k, v]) => [
                k,
                {
                  value: v,
                  original:
                    existingRowOverrides[k]?.original ??
                    originalRow?.[k] ??
                    oldRow[k],
                },
              ])
          ),
        },
      },
    };
    dispatch(SOVStore.updateSOV(newOverrides));
  };

  const deleteOverride = (rowId, colId) => {
    const over = sovInput?.overrides ?? {};
    const row = over[rowId] ?? {};
    const cell = row[colId];
    if (!cell) return;
    const newRow = Object.entries(row).filter((r) => r[0] !== colId);
    const delRow = newRow.length === 0;
    const newOverrides = {
      overrides: {
        ...(delRow
          ? Object.fromEntries(
              Object.entries(over).filter((r) => r[0] !== rowId)
            )
          : over),
      },
    };
    if (!delRow) newOverrides.overrides[rowId] = Object.fromEntries(newRow);
    dispatch(SOVStore.updateSOV(newOverrides));
  };

  useEffect(() => {
    const fileId = sovInput?.extractedDataFileId;
    if (!fileId) return;

    const attachIds = (rows) =>
      rows.map((row, index) => ({
        ...row,
        id: `${fileId}:${index}`,
        sovFileId: fileId,
      }));

    const existing = sovInput?._resolvedLocations;
    // Recover submissions that were saved before _resolvedLocations was stripped:
    // their cached rows may be present but missing id/sovFileId.
    if (existing?.length && !existing[0]?.id) {
      dispatch(SOVStore.updateSOV({ _resolvedLocations: attachIds(existing) }));
      return;
    }
    if (existing) return;

    const loadExtractionData = async () => {
      try {
        const loadedData = await retrieveSovExtractionData(fileId);
        // S3-stored extraction data doesn't include id/sovFileId — reattach
        // them here so row lookups (overrides, pricing) work correctly.
        dispatch(
          SOVStore.updateSOV({ _resolvedLocations: attachIds(loadedData) })
        );
      } catch (error) {}
    };

    loadExtractionData();
  }, [sovInput?.extractedDataFileId, sovInput?._resolvedLocations, dispatch]);

  const handleDownloadTableCSV = useCallback(() => {
    const baseFilename =
      sovInput?.originalFilename?.replace(/\.[^/.]+$/, "") || "sov";
    const filename = `${baseFilename}_table_data.csv`;

    if (rows.length === 0) {
      return;
    }

    const header = CSV_COLUMNS.map((col) => col.name).join(",");

    const csvRows = rows.map((row) =>
      CSV_COLUMNS.map((col) => {
        const value = row[col.key];
        if (value == null) return "";
        const stringValue = String(value);
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(",")
    );

    const csvContent = [header, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setDownloadMenuAnchor(null);
  }, [rows, sovInput?.originalFilename]);

  const handleDownloadPingJSON = useCallback(async () => {
    try {
      const downloadUrl = await fileUtils.getDownloadUrl(
        sovInput.rawExtractionFileId
      );

      const response = await fetch(downloadUrl);
      const jsonData = await response.json();

      const baseFilename =
        sovInput?.originalFilename?.replace(/\.[^/.]+$/, "") ||
        "sov_extraction";
      const filename = `${baseFilename}_ping_extraction.json`;

      const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloadMenuAnchor(null);
    } catch (error) {}
  }, [sovInput]);

  useEffect(() => {
    if (!isPingExtractionEnabled) return;

    const jobId = sovInput?.extractionJobId;
    const isComplete = sovInput?.extractionComplete;

    if (!jobId || isComplete) {
      return;
    }

    let isMounted = true;
    let timeoutId = null;

    const checkStatus = async () => {
      try {
        const statusResponse = await aiAgentsService.SovFixerService.checkStatus(
          jobId
        );

        if (!isMounted) return;

        setExtractionProgress({
          status: statusResponse.status,
          pct_complete: statusResponse.pct_complete || 0,
        });

        if (statusResponse.complete) {
          const dataResponse = await aiAgentsService.SovFixerService.getData(
            jobId
          );

          if (!isMounted) return;

          const extractedBuildings = dataResponse?.data?.buildings || [];
          const modalData = extractedBuildings.map(transformSovFixerToModal);

          const baseFilename =
            sovInput?.originalFilename?.replace(/\.[^/.]+$/, "") ||
            "sov_extraction";
          const jsonFile = new File(
            [JSON.stringify(dataResponse.data, null, 2)],
            `${baseFilename}_ping_extraction.json`,
            { type: "application/json" }
          );

          const rawDataFileId = await fileUtils.createFileEntry(jsonFile);

          const internalData = modalData.map((row, index) => {
            const converted = convertFromModalFormat(row);
            return {
              ...converted,
              rowIndex: index,
            };
          });

          const { fileId: extractedDataFileId } = await storeSovExtractionData(
            internalData,
            sovInput?.originalFilename
          );

          const dataWithIds = internalData.map((row, index) => ({
            ...row,
            id: `${extractedDataFileId}:${index}`,
            sovFileId: extractedDataFileId,
          }));

          dispatch(
            SOVStore.setSOV({
              sovFileId: extractedDataFileId,
              _resolvedLocations: dataWithIds,
              extractedDataFileId,
              extractionComplete: true,
              extractionJobId: null,
              rawExtractionFileId: rawDataFileId,
              originalFilename: sovInput?.originalFilename,
            })
          );

          clearExtractionState(submissionId);

          setExtractionProgress(null);
        } else {
          if (isMounted) {
            timeoutId = setTimeout(checkStatus, 5000);
          }
        }
      } catch (error) {
        if (!isMounted) return;

        setExtractionProgress(null);

        if (isMounted) {
          timeoutId = setTimeout(checkStatus, 10000);
        }
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    sovInput?.extractionJobId,
    sovInput?.extractionComplete,
    sovInput?.originalFilename,
    dispatch,
    submissionId,
    isPingExtractionEnabled,
  ]);

  const handleExtractionUpload = useCallback(
    async (fileId, filename) => {
      if (!isPingExtractionEnabled) return;

      try {
        setIsExtracting(true);
        setExtractionProgress({ status: "SUBMITTING", pct_complete: 0 });

        const fileUrlResponse = await utils.authenticatedFetch(
          config.endpoints.files(`get-download-url/${fileId}`)
        );
        const { url: fileUrl } = await fileUrlResponse.json();

        const submitResponse = await aiAgentsService.SovFixerService.submitExtraction(
          fileUrl
        );

        dispatch(
          SOVStore.updateSOV({
            originalFilename: filename,
            extractionJobId: submitResponse.job_id,
            extractionComplete: false,
          })
        );

        saveExtractionState(submissionId, {
          originalFilename: filename,
          extractionJobId: submitResponse.job_id,
          extractionComplete: false,
        });

        setIsExtracting(false);
        setExtractionProgress(null);
      } catch (error) {
        setIsExtracting(false);
        setExtractionProgress(null);
      }
    },
    [dispatch, submissionId, isPingExtractionEnabled]
  );

  const hasSovInput = sovInput?.sovFileId || sovInput?.extractedDataFileId;
  const hasActiveExtraction =
    sovInput?.extractionJobId && !sovInput?.extractionComplete;
  const isExtractionBasedSov = Boolean(sovInput?.extractedDataFileId);

  const activeSwitches = useMemo(
    () => [
      ...(sovConfig?.switches ?? []),
      ...Object.keys(sovInput?.switches ?? {})
        .filter(
          (key) =>
            !(sovConfig?.switches ?? []).map(({ key }) => key).includes(key)
        )
        .map((key) => ({
          key,
          label: key
            .split(/[_-]/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        })),
    ],
    [sovConfig, sovInput]
  );

  const columnsConfig = useMemo(() => {
    const baseColumns = sovConfig?.columns ?? [];

    if (!isExtractionBasedSov) {
      return baseColumns;
    }

    const hasLatColumn = baseColumns.some((col) => col.key === "_LAT");
    const hasLngColumn = baseColumns.some((col) => col.key === "_LNG");

    if (hasLatColumn && hasLngColumn) {
      return baseColumns;
    }

    const zipIndex = baseColumns.findIndex((col) => col.key === "_ZIP_CODE");
    const insertIndex = zipIndex >= 0 ? zipIndex + 1 : baseColumns.length;

    const latLngColumns = [];
    if (!hasLatColumn) {
      latLngColumns.push({
        key: "_LAT",
        name: "Lat",
        width: 100,
        type: "number",
        editable: true,
      });
    }
    if (!hasLngColumn) {
      latLngColumns.push({
        key: "_LNG",
        name: "Long",
        width: 100,
        type: "number",
        editable: true,
      });
    }

    return [
      ...baseColumns.slice(0, insertIndex),
      ...latLngColumns,
      ...baseColumns.slice(insertIndex),
    ];
  }, [sovConfig?.columns, isExtractionBasedSov]);

  const columns = useMemo(
    () =>
      columnsConfig.filter((column) => {
        if (column.hideOnSwitch && sovInput?.switches?.[column.hideOnSwitch]) {
          return false;
        }
        if (column.showOnSwitch && !sovInput?.switches?.[column.showOnSwitch]) {
          return false;
        }
        return true;
      }),
    [columnsConfig, sovInput]
  );

  if (isExtracting || hasActiveExtraction) {
    return (
      <Component title={"Extracting SOV Data"} isFetching={true}>
        <Stack
          direction={"column"}
          spacing={3}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{
            minHeight: "40vh",
            width: "100%",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <BigLoader />
          {extractionProgress && (
            <>
              <Typography variant={"body1"} sx={{ textAlign: "center" }}>
                {extractionProgress.status === "SUBMITTING"
                  ? "Submitting file for extraction..."
                  : extractionProgress.status === "RESTORING"
                  ? "Resuming extraction from page reload..."
                  : extractionProgress.status === "IN_PROGRESS"
                  ? `Processing... ${extractionProgress.pct_complete}% complete`
                  : extractionProgress.status}
              </Typography>
              {extractionProgress.status === "IN_PROGRESS" && (
                <Stack direction={"column"} spacing={1} sx={{ width: "100%" }}>
                  <LinearProgress
                    variant={"determinate"}
                    value={extractionProgress.pct_complete || 0}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                  <Typography
                    variant={"caption"}
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    {`${extractionProgress.pct_complete || 0}% complete`}
                  </Typography>
                </Stack>
              )}
            </>
          )}
          {hasActiveExtraction && !extractionProgress && (
            <Typography variant={"body1"}>
              {"Checking extraction status..."}
            </Typography>
          )}
        </Stack>
      </Component>
    );
  }

  if (!hasSovInput) {
    return (
      <UploadSov
        onExtractionUpload={
          isPingExtractionEnabled ? handleExtractionUpload : undefined
        }
        showExtractionMode={isPingExtractionEnabled}
      />
    );
  }

  return (
    <>
      <Component
        title={"SOV"}
        isFetching={isProgramPricingFetching}
        options={
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            <NonCatValidationSummary
              validations={validations}
              isFiltered={isFilteredToNonCatErrors}
              setIsFiltered={setIsFilteredToNonCatErrors}
            />
            {sovInput?.rawExtractionFileId ? (
              <>
                <Button
                  size={"small"}
                  isDisabled={!hasSovInput}
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon />}
                >
                  {"Download"}
                </Button>
                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={() => setDownloadMenuAnchor(null)}
                >
                  <MenuItem onClick={handleDownloadTableCSV}>
                    {"Download Table Data (CSV)"}
                  </MenuItem>
                  <MenuItem onClick={handleDownloadPingJSON}>
                    {"Download Ping Extraction (JSON)"}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <DownloadSOVButton />
            )}
            {hasSovInput && (
              <Disabled ifReadOnly>
                <Button
                  size={"small"}
                  disableElevation
                  variant={"contained"}
                  color={"secondary"}
                  onClick={() => dispatch(SOVStore.setSOV({}))}
                >
                  {"Clear"}
                </Button>
              </Disabled>
            )}
            <Visible byTag={"input.sov.validateForLayer"} defaultVisible>
              <FormControl variant={"filled"} size={"small"}>
                <InputLabel>{"Validate for Layer"}</InputLabel>
                <Select
                  value={validationLayerId ?? "__NONE__"}
                  onChange={(e) => setValidationLayerId(e.target.value)}
                  sx={{
                    minWidth: 180,
                  }}
                >
                  <MenuItem value={"__NONE__"}>{"Ground-Up"}</MenuItem>
                  {layers.map((layer) => {
                    return (
                      <MenuItem key={layer.id} value={layer.id}>
                        {utils.shortLayerName(layer)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Visible>
          </Stack>
        }
      >
        {isResolvedSovFetching ? (
          <BigLoader />
        ) : (
          <Stack direction={"column"} spacing={2}>
            <Visible byTag={"<SOV>.heatmap"} defaultVisible={true}>
              <SovHeatmap
                sov={resolvedSov}
                loading={isProgramPricingFetching}
              />
            </Visible>
            <SovMetrics locations={rows} />
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography variant={"h6"}>{"Locations"}</Typography>
              {activeSwitches.length > 0 && (
                <>
                  <IconButton
                    icon={MoreVertIcon}
                    onClick={(e) => setLocationsMenuAnchor(e.currentTarget)}
                  />
                  <Menu
                    anchorEl={locationsMenuAnchor}
                    open={Boolean(locationsMenuAnchor)}
                    onClose={() => setLocationsMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    {activeSwitches.map((activeSwitch) => (
                      <MenuItem disableRipple key={activeSwitch.key}>
                        <Disabled ifReadOnly>
                          <FormControlLabel
                            control={
                              <Switch
                                size={"small"}
                                checked={sovInput?.switches?.[activeSwitch.key]}
                                onChange={(e) =>
                                  dispatch(
                                    SOVStore.updateSOV({
                                      switches: {
                                        ...sovInput?.switches,
                                        [activeSwitch.key]: e.target.checked,
                                      },
                                    })
                                  )
                                }
                              />
                            }
                            label={activeSwitch.label}
                          />
                        </Disabled>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
            <Disabled ifReadOnly>
              <SovGrid
                columnsConfig={columns}
                rows={filteredRows}
                enumConfig={{
                  enums: sovConfig?.enums ?? {},
                }}
                onRowUpdate={updateOverrides}
                onCellValueDelete={deleteOverride}
                overrides={sovInput?.overrides}
                validations={validations}
                isFetching={isProgramPricingFetching}
                fillColumn={fillColumn}
              />
            </Disabled>
          </Stack>
        )}
      </Component>
    </>
  );
};

export default SOV;
